import React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import FileDownload from 'js-file-download'
import { toast } from 'react-toastify'

import { axiosInstance } from '../helpers'

export type RecordsOccupancy = 'occupied' | 'future' | 'vacant'

export interface RecordsUnitRow {
    id: number
    name: string
    folder_path: string
    occupied: boolean
    occupancy?: RecordsOccupancy
    open_service_request_count: number
    last_work_at: string | null
}

export interface RecordsUnitsSummary {
    unit_count: number
    occupied_count: number
    future_count?: number
    vacant_count?: number
    open_service_request_count: number
}

export interface RecordsUnitsResponse {
    summary: RecordsUnitsSummary
    results: RecordsUnitRow[]
}

export interface RecordsServiceRequestCategory {
    key: string
    label: string
    open: number
    completed: number
    total: number
}

export interface RecordsServiceRequestRow {
    id: number
    issue_title: string
    description: string
    status: string
    category: string
    created_date: string | null
    unit_id: number | null
    unit_name: string
    folder_path: string
    tenant_name: string
}

export interface RecordsServiceRequestSlice {
    open_count: number
    completed_count: number
    total: number
    categories: RecordsServiceRequestCategory[]
    requests: RecordsServiceRequestRow[]
}

export const useRecordsUnits = (enabled = true) => {
    return useQuery<RecordsUnitsResponse, Error>({
        queryKey: ['records-units'],
        queryFn: async () => {
            const res = await axiosInstance.get<RecordsUnitsResponse>(
                'records/units/',
            )
            return res.data
        },
        enabled,
        refetchOnWindowFocus: false,
    })
}

export const useRecordsServiceRequestSlice = (params: {
    category?: string | null
    openOnly?: boolean
    unitId?: number | null
    enabled?: boolean
}) => {
    const category = params.category || undefined
    const openOnly = Boolean(params.openOnly)
    const unitId = params.unitId ?? undefined
    const enabled = params.enabled !== false

    return useQuery<RecordsServiceRequestSlice, Error>({
        queryKey: [
            'records-sr-slice',
            category ?? null,
            openOnly,
            unitId ?? null,
        ],
        queryFn: async () => {
            const res = await axiosInstance.get<RecordsServiceRequestSlice>(
                'records/slice/service-requests/',
                {
                    params: {
                        ...(category ? { category } : {}),
                        ...(openOnly ? { open_only: true } : {}),
                        ...(unitId != null ? { unit_id: unitId } : {}),
                    },
                },
            )
            return res.data
        },
        enabled,
        refetchOnWindowFocus: false,
    })
}

export type RecordsUnitOccupant = {
    user_id: number | null
    name: string
    area_id: number | null
    area_label: string
    lease_id: number
    lease_status: string
}

export type RecordsUnitBed = {
    id: number
    label: string
    occupiable: boolean
}

export type RecordsUnitFile = {
    id: number
    name: string
    folder_path: string
    occupied: boolean
    open_service_request_count: number
    last_work_at: string | null
    beds: RecordsUnitBed[]
    occupants: RecordsUnitOccupant[]
    future_occupants?: RecordsUnitOccupant[]
}

export type RecordsTimelineKind =
    | 'lease'
    | 'service_request'
    | 'staff_request'
    | 'inspection'
    | 'move_in_inspection'
    | 'workorder'
    | 'message'
    | 'note'
    | 'damage'
    | 'incident'
    | 'pet'

export type RecordsTimelineEvent = {
    at: string | null
    kind: RecordsTimelineKind | string
    title: string
    detail: string
    source: string
    source_id: number
    unit_id: number | null
    user_id: number | null
    status: string | null
    area_id: number | null
    area_label: string
    vendor?: string
    service_id?: number | null
    service_name?: string
    service_color?: string | null
    start_date?: string | null
    end_date?: string | null
    unit_name?: string
    associated_areas?: string[]
}

export type RecordsTimelineResponse = {
    results: RecordsTimelineEvent[]
}

export const useRecordsUnitFile = (unitId: number | null, enabled = true) => {
    return useQuery<RecordsUnitFile, Error>({
        queryKey: ['records-unit', unitId],
        queryFn: async () => {
            const res = await axiosInstance.get<RecordsUnitFile>(
                `records/units/${unitId}/`,
            )
            return res.data
        },
        enabled: enabled && unitId != null,
        refetchOnWindowFocus: false,
        retry: false,
    })
}

export const useRecordsUnitTimeline = (
    unitId: number | null,
    enabled = true,
) => {
    return useQuery<RecordsTimelineResponse, Error>({
        queryKey: ['records-unit-timeline', unitId],
        queryFn: async () => {
            const res = await axiosInstance.get<RecordsTimelineResponse>(
                `records/units/${unitId}/timeline/`,
            )
            return res.data
        },
        enabled: enabled && unitId != null,
        refetchOnWindowFocus: false,
        retry: false,
    })
}

export type RecordsPersonStatus =
    | 'current'
    | 'future'
    | 'past'
    | 'cancelled'
    | 'incoming'
    | 'former'

export type RecordsPersonRow = {
    user_id: number | null
    lead_id?: number | null
    name: string
    email: string
    phone: string
    status: RecordsPersonStatus | string
    unit_id: number | null
    unit_name: string
    folder_path: string
    area_label: string
}

export type RecordsPeopleResponse = {
    results: RecordsPersonRow[]
    query?: string
    cohort?: string
    count?: number
    page?: number
    page_size?: number
    page_count?: number
    truncated?: boolean
}

export type RecordsPersonUnit = {
    unit_id: number | null
    unit_name: string
    folder_path: string
    area_id: number | null
    area_label: string
    lease_id: number
    lease_status: string
    current: boolean
}

export type RecordsPersonFile = {
    user_id: number | null
    name: string
    email: string
    phone: string
    status: RecordsPersonStatus | string
    open_service_request_count: number
    lead_id: number | null
    current_units: RecordsPersonUnit[]
    future_units?: RecordsPersonUnit[]
    past_units: RecordsPersonUnit[]
}

export const useRecordsPeople = (params: {
    q?: string | null
    cohort?: string | null
    page?: number
    enabled?: boolean
}) => {
    const query = (params.q || '').trim()
    const searching = query.length >= 2
    const cohort = params.cohort || 'current'
    const page = params.page && params.page > 1 ? params.page : 1
    const enabled = params.enabled !== false

    return useQuery<RecordsPeopleResponse, Error>({
        queryKey: [
            'records-people',
            searching ? query : `cohort:${cohort}:page:${page}`,
        ],
        queryFn: async () => {
            if (searching) {
                const res = await axiosInstance.get<RecordsPeopleResponse>(
                    'records/people/search/',
                    { params: { q: query } },
                )
                return res.data
            }
            const res = await axiosInstance.get<RecordsPeopleResponse>(
                'records/people/',
                { params: { cohort, page } },
            )
            return res.data
        },
        enabled,
        keepPreviousData: !searching,
        refetchOnWindowFocus: false,
    })
}

export const useRecordsPersonFile = (
    userId: number | null,
    leadId: number | null = null,
    enabled = true,
) => {
    return useQuery<RecordsPersonFile, Error>({
        queryKey: ['records-person', userId, leadId],
        queryFn: async () => {
            const path =
                userId != null
                    ? `records/people/${userId}/`
                    : `records/leads/${leadId}/`
            const res = await axiosInstance.get<RecordsPersonFile>(path)
            return res.data
        },
        enabled: enabled && (userId != null || leadId != null),
        refetchOnWindowFocus: false,
        retry: false,
    })
}

export const useRecordsPersonTimeline = (
    userId: number | null,
    leadId: number | null = null,
    enabled = true,
) => {
    return useQuery<RecordsTimelineResponse, Error>({
        queryKey: ['records-person-timeline', userId, leadId],
        queryFn: async () => {
            const path =
                userId != null
                    ? `records/people/${userId}/timeline/`
                    : `records/leads/${leadId}/timeline/`
            const res = await axiosInstance.get<RecordsTimelineResponse>(path)
            return res.data
        },
        enabled: enabled && (userId != null || leadId != null),
        refetchOnWindowFocus: false,
        retry: false,
    })
}

export type RecordsPersonNote = {
    id: number
    body: string
    created_by: number | null
    created_by_name: string
    created_date: string | null
    source: string
    source_id: number
    lead_id: number | null
}

export type RecordsPersonNotesResponse = {
    results: RecordsPersonNote[]
}

const recordsPersonNotesPath = (userId: number | null, leadId: number | null) =>
    userId != null
        ? `records/people/${userId}/notes/`
        : `records/leads/${leadId}/notes/`

export const useRecordsPersonNotes = (
    userId: number | null,
    leadId: number | null = null,
    enabled = true,
) => {
    const queryClient = useQueryClient()
    const query = useQuery<RecordsPersonNotesResponse, Error>({
        queryKey: ['records-person-notes', userId, leadId],
        queryFn: async () => {
            const res = await axiosInstance.get<RecordsPersonNotesResponse>(
                recordsPersonNotesPath(userId, leadId),
            )
            return res.data
        },
        enabled: enabled && (userId != null || leadId != null),
        refetchOnWindowFocus: false,
        retry: false,
    })

    const addNote = useMutation({
        mutationFn: async (body: string) => {
            const res = await axiosInstance.post<RecordsPersonNote>(
                recordsPersonNotesPath(userId, leadId),
                { body },
            )
            return res.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['records-person-notes', userId, leadId],
            })
            queryClient.invalidateQueries({
                queryKey: ['records-person-timeline', userId, leadId],
            })
            toast.success('Note saved')
        },
        onError: () => {
            toast.error('Unable to save note')
        },
    })

    return {
        notes: query.data?.results ?? [],
        isLoading: query.isLoading,
        isError: query.isError,
        addNote: addNote.mutateAsync,
        isSaving: addNote.isLoading,
    }
}

export type RecordsTrailExportFormat = 'xlsx' | 'pdf'

const recordsTrailExportPath = (opts: {
    userId?: number | null
    leadId?: number | null
    unitId?: number | null
}) => {
    if (opts.unitId != null) {
        return `records/units/${opts.unitId}/timeline/export/`
    }
    if (opts.userId != null) {
        return `records/people/${opts.userId}/timeline/export/`
    }
    return `records/leads/${opts.leadId}/timeline/export/`
}

const filenameFromDisposition = (value: string | undefined) => {
    if (!value) return null
    const match = /filename="([^"]+)"/.exec(value)
    return match ? match[1] : null
}

export const useRecordsTrailExport = (opts: {
    userId?: number | null
    leadId?: number | null
    unitId?: number | null
    kinds?: string | null
    enabled?: boolean
}) => {
    const [
        busyFormat,
        setBusyFormat,
    ] = React.useState<RecordsTrailExportFormat | null>(null)
    const canExport =
        opts.enabled !== false &&
        (opts.unitId != null || opts.userId != null || opts.leadId != null)

    const download = async (format: RecordsTrailExportFormat) => {
        if (!canExport || busyFormat) return
        setBusyFormat(format)
        try {
            const res = await axiosInstance.get(recordsTrailExportPath(opts), {
                params: {
                    format,
                    ...(opts.kinds ? { kinds: opts.kinds } : {}),
                },
                responseType: 'blob',
            })
            const filename =
                filenameFromDisposition(res.headers['content-disposition']) ||
                `records-audit-trail.${format}`
            FileDownload(new Blob([res.data]), filename)
        } catch {
            toast.error('Unable to export the audit trail')
        } finally {
            setBusyFormat(null)
        }
    }

    return {
        download,
        busyFormat,
        isExporting: busyFormat != null,
        canExport,
    }
}
